import { createContext, useContext, useEffect, useState } from 'react';

type ExtendedEthereumProvider = typeof window.ethereum & {
  on?: (event: string, handler: (...args: any[]) => void) => void;
  request?: (args: { method: string; params?: any[] }) => Promise<any>;
};

interface WalletContextType {
  accountAddress: string | null;
  publicKey: any | null;
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  isConnected: boolean;
  chainId: string | null; // Add chainId to the context
}

const WalletContext = createContext<WalletContextType>({
  accountAddress: null,
  publicKey: null,
  connect: async () => {},
  disconnect: async () => {},
  isConnected: false,
  chainId: null,
});

export const useWallet = () => useContext(WalletContext);

export const WalletProvider = ({ children }: { children: React.ReactNode }) => {
  const [accountAddress, setAccountAddress] = useState<string | null>(null);
  const [publicKey, setPublicKey] = useState<any | null>(null);
  const [chainId, setChainId] = useState<string | null>(null); // Track the current chain ID

  const getEthereumProvider = (): ExtendedEthereumProvider | null => {
    if (typeof window !== 'undefined' && 'ethereum' in window) {
      const provider = window.ethereum as ExtendedEthereumProvider;
      if (provider?.isMetaMask) return provider;
    }
    return null;
  };

  const connect = async () => {
    const provider = getEthereumProvider();

    if (!provider) {
      alert('MetaMask not found. Please install it.');
      return;
    }

    try {
      console.log('Requesting accounts from MetaMask...');
      const accounts = await provider.request?.({ method: 'eth_requestAccounts' });

      if (accounts && accounts.length > 0) {
        console.log('Connected account:', accounts[0]);
        setAccountAddress(accounts[0]);
        setPublicKey(accounts[0]);
      } else {
        console.error('No accounts found after request');
      }

      // Get the current chain ID
      const currentChainId = await provider.request({ method: 'eth_chainId' });
      setChainId(currentChainId); // Update chain ID state
    } catch (err) {
      // Improved error handling for debugging
      console.error('Connection failed:', err);
      setAccountAddress(null);
      setPublicKey(null);
    }
  };

  const disconnect = async () => {
    // MetaMask does not support programmatic disconnection
    setAccountAddress(null);
    setPublicKey(null);
    setChainId(null); // Clear the chain ID
  };

  useEffect(() => {
    const provider = getEthereumProvider();

    if (provider) {
      // Handle accountsChanged
      provider?.on?.('accountsChanged', (accounts: string[]) => {
        if (accounts.length === 0) {
          setAccountAddress(null);
          setPublicKey(null);
        } else {
          setAccountAddress(accounts[0]);
          setPublicKey(accounts[0]);
        }
      });

      // Handle chainChanged
      provider?.on?.('chainChanged', (newChainId: string) => {
        setChainId(newChainId); // Update the chainId when the network changes
        console.log('Network changed to:', newChainId);
      });

      // Handle disconnect event
      provider?.on?.('disconnect', () => {
        setAccountAddress(null);
        setPublicKey(null);
        setChainId(null);
      });
    }

    // Cleanup event listeners on component unmount
    return () => {
      const provider = getEthereumProvider();
      provider?.off?.('accountsChanged', () => {});
      provider?.off?.('chainChanged', () => {});
      provider?.off?.('disconnect', () => {});
    };
  }, []);

  return (
    <WalletContext.Provider
      value={{
        accountAddress,
        publicKey,
        connect,
        disconnect,
        isConnected: !!accountAddress,
        chainId, // Pass chainId into the context
      }}
    >
      {children}
    </WalletContext.Provider>
  );
};
